import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Home } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import FeaturedHeader from "../components/FeaturedHeader";
import {
  formatJobDate,
  getJobSalaryLabel,
  getJobSummary,
  getJobTitle,
  listJobs,
} from "../api/jobs";

function JobCard({ job, isAr, lang }) {
  const title = getJobTitle(job, isAr);
  const summary = getJobSummary(job, isAr);
  const image =
    job?.cover_image ||
    "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80";

  return (
    <div className="col-12 col-md-6 col-lg-4">
      <div className="course-card-new">
        <div className="course-card-new__image-wrapper">
          <img src={image} alt={title} className="course-card-new__image" />

          <div className="course-card-new__badges">
            <span className="badge-level">{job?.employment_type || (isAr ? "دوام كامل" : "full-time")}</span>
            <span className="badge-price">
              {job?.is_featured ? (isAr ? "مميزة" : "Featured") : getJobSalaryLabel(job, isAr)}
            </span>
          </div>
        </div>

        <div className="course-card-new__content">
          <div className="course-card-new__meta">
            <span className="meta-item">
              <i className="bi bi-building"></i>
              {job?.company || (isAr ? "شركة" : "Company")}
            </span>
            <span className="meta-item">
              <i className="bi bi-geo-alt"></i>
              {job?.location || (isAr ? "الموقع" : "Location")}
            </span>
          </div>

          <h3 className="course-card-new__title">{title}</h3>
          <p className="course-card-new__description">{summary}</p>

          <div className="course-card-new__rating">
            <i className="bi bi-people"></i>
            <span>{job?.applications_count || 0} {isAr ? "متقدم" : "applicants"}</span>
          </div>

          <div className="course-card-new__instructor">
            <i className="bi bi-hourglass-split"></i>
            <span>
              {job?.application_deadline
                ? formatJobDate(job.application_deadline, lang)
                : isAr ? "التقديم مفتوح" : "Open application"}
            </span>
          </div>

          <Link
            to={`/jobs/${job?.slug || job?.id}`}
            className="course-card-new__button"
          >
            {isAr ? "عرض الوظيفة" : "Apply Now"}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function JobsPage() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        const data = await listJobs();
        if (active) {
          setJobs(data);
        }
      } catch (err) {
        logger.error("Jobs load error", err);
        if (active) {
          setError(isAr ? "فشل تحميل الوظائف." : "Failed to load jobs.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [isAr]);

  const filteredJobs = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return jobs;

    return jobs.filter((job) => {
      const haystack = [
        job?.title_ar,
        job?.title_en,
        job?.short_description_ar,
        job?.short_description_en,
        job?.company,
        job?.location,
        job?.employment_type,
        job?.experience_level,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [jobs, search]);

  return (
    <div dir={isAr ? "rtl" : "ltr"}>
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to="/" style={{textDecoration: 'none', color: 'inherit'}}>
              <Home size={18} strokeWidth={1.5} style={{display: 'inline', marginRight: '6px'}} /> {isAr ? "الرئيسية" : "Home"}
            </Link>
          </li>
          <li className="breadcrumb-item" style={{margin: '0 4px'}}>
            <span style={{color: '#7B8794'}}>&gt;</span>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {isAr ? "الوظائف" : "Jobs"}
          </li>
        </ol>
      </nav>

      <section className="course-hero-navy">
        <div>
          <h1 className="display-5 fw-bold text-white">
            {isAr ? "اكتشف الوظائف" : "Find Your Job"}
          </h1>
          <p className="lead text-white-75">
            {isAr
              ? "تصفح آلاف الفرص الوظيفية المتاحة من أفضل الشركات"
              : "Browse thousands of job opportunities from top companies"}
          </p>
        </div>

        <div className="card border-0 rounded-4">
          <div className="card-body">
            <label className="form-label fw-semibold">
              {isAr ? "ابحث عن وظيفة" : "Search jobs"}
            </label>
            <div className="input-group">
              <input
                type="search"
                className="form-control form-control-lg"
                placeholder={isAr ? "المسمى أو الشركة أو الموقع" : "Role, company, or location"}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <span className="input-group-text bg-white border-0">
                <i className="bi bi-search"></i>
              </span>
            </div>
            <div className="d-flex justify-content-between text-muted small mt-3">
              <span>{jobs.length} {isAr ? "وظيفة" : "jobs"}</span>
              <span>{filteredJobs.length} {isAr ? "نتيجة" : "results"}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="container py-4 py-lg-5">
        <FeaturedHeader
          title={isAr ? "الوظائف المميزة" : "Featured Jobs"}
          subtitle={isAr ? "اكتشف أفضل الوظائف" : "Discover top-rated jobs"}
          isAr={isAr}
        />

      {loading ? (
        <div className="py-5 text-center">
          <div className="spinner-border text-primary mb-3" role="status" />
          <p className="mb-0">{isAr ? "جارِ تحميل الوظائف..." : "Loading jobs..."}</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger rounded-4">{error}</div>
      ) : filteredJobs.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body py-5 text-center">
            <h2 className="h4 fw-bold mb-2">
              {isAr ? "لا توجد وظائف مطابقة" : "No matching jobs"}
            </h2>
            <p className="text-muted mb-0">
              {isAr ? "جرّب كلمات بحث مختلفة." : "Try a different search term."}
            </p>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          {filteredJobs.map((job) => (
            <JobCard key={job.id} job={job} isAr={isAr} lang={lang} />
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
